import { useState, useEffect, useCallback } from "react";
import { MapPin, CheckCircle2, ArrowRight, Navigation, Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { REGIONS, useLocation, ALL_LA_SUB_REGIONS } from "@/lib/locations";
import { Button } from "./ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { findNearestRegion } from "@/hooks/use-geo-location";
import { toast } from "sonner";
import { ZipCodeChecker } from "./ZipCodeChecker";



export const ServiceAreaMap = () => {
  const location = useLocation();
  // const navigate = useNavigate();
  
  const [selectedRegionSlug, setSelectedRegionSlug] = useState<string>(location.regionSlug || "sacramento-area");
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);

  useEffect(() => {
    if (location.regionSlug) {
      setSelectedRegionSlug(location.regionSlug);
    }
  }, [location.regionSlug]);

  const handleLocateMe = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const nearest = findNearestRegion(latitude, longitude);
        
        if (nearest && REGIONS[nearest]) {
          setSelectedRegionSlug(nearest);
          toast.success(`Found you! Showing service areas near ${REGIONS[nearest].name}.`);
        } else {
          toast.error("You appear to be outside our current service areas.");
        }
        setIsLocating(false);
      },
      (error) => {
        toast.error("Unable to retrieve your location. Please check your permissions.");
        setIsLocating(false);
      },
      { timeout: 10000 }
    );
  }, []);

  const currentRegion = REGIONS[selectedRegionSlug] || REGIONS["sacramento-area"];
  const cities = currentRegion.cities || [];
  const isLA = selectedRegionSlug === 'los-angeles';

  return (
    <section className="py-24 bg-muted/30 overflow-hidden relative">
      <div className="container relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Left: City list & Details */}
          <div className="flex-1 space-y-8 w-full">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-bold text-sm">
              <MapPin className="h-4 w-4" />
              <span>Interactive Coverage Map</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold leading-tight">
              Serving the Greater <span className="text-primary">{currentRegion.name}</span> Area
            </h2>
            
            <p className="text-xl text-muted-foreground leading-relaxed">
              Select a region on the map to explore our service areas. From the heart of the city to the surrounding suburbs, our dedicated teams are ready to make your home sparkle.
            </p>

            {isLA && (
              <div className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h3 className="text-sm font-bold mb-4 text-muted-foreground uppercase tracking-wider">Browse by Neighborhood</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {ALL_LA_SUB_REGIONS.map((sub) => (
                    <Link
                      key={sub.slug}
                      to={`/locations/la/${sub.slug}`}
                      className="flex flex-col p-4 rounded-xl border bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all group shadow-sm"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="h-4 w-4 text-primary group-hover:text-primary-foreground transition-colors" />
                        <h4 className="font-bold text-sm">{sub.shortName}</h4>
                      </div>
                      <p className="text-xs text-muted-foreground group-hover:text-primary-foreground/80 transition-colors line-clamp-1">
                        {sub.cities.join(", ")}
                      </p>
                    </Link>
                  ))}
                </div>
                <div className="relative flex items-center gap-4 mb-4">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Or browse by city</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500" key={selectedRegionSlug}>
              {cities.map((city) => (
                <Link
                  key={city}
                  to={`/locations/${city.toLowerCase().replace(/\s+/g, "-")}`}
                  onMouseEnter={() => setHoveredCity(city)}
                  onMouseLeave={() => setHoveredCity(null)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group border ${
                    hoveredCity === city
                      ? "bg-primary/10 text-primary border-primary/30 shadow-sm"
                      : "bg-background hover:bg-primary/5 hover:text-primary border-transparent hover:border-primary/20"
                  }`}
                >
                  <CheckCircle2 className={`h-4 w-4 flex-shrink-0 transition-all ${hoveredCity === city ? "text-primary scale-110" : "text-primary/40 group-hover:text-primary group-hover:scale-110"}`} />
                  <span className="font-medium text-sm truncate">{city}</span>
                </Link>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button asChild size="lg" className="rounded-xl font-bold w-full sm:w-auto">
                <Link to="/book">
                  Book in {currentRegion.name} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
          {/* Right: Local Interactive Map */}
          <div className="flex-1 w-full relative max-w-[600px] mx-auto h-[450px] md:h-[600px] animate-in fade-in slide-in-from-right-8 duration-700">
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 to-transparent rounded-3xl -z-10" />
            <div className="bg-card border shadow-xl rounded-3xl overflow-hidden h-full flex flex-col">
              <div className="p-4 md:p-6 border-b bg-muted/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    {currentRegion.name} Coverage
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">Interactive local map</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleLocateMe}
                    disabled={isLocating}
                    className="h-8 rounded-full border-primary/20 hover:bg-primary/5 text-primary"
                  >
                    {isLocating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Navigation className="h-3.5 w-3.5 mr-1.5" />}
                    Locate Me
                  </Button>
                  <div className="flex items-center gap-2 text-xs font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    Live
                  </div>
                </div>
              </div>
              <div className="flex-1 w-full relative bg-primary/5 overflow-hidden group">
                {/* Brand color tint overlay */}
                <div className="absolute inset-0 bg-primary mix-blend-color opacity-20 pointer-events-none z-10 transition-opacity duration-700 group-hover:opacity-0" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent pointer-events-none z-10" />
                
                {/* Custom Map Markers Overlay (Decorative) */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none flex flex-col items-center justify-center transition-all duration-500 scale-100 group-hover:scale-0 group-hover:opacity-0">
                  <div className="bg-primary text-primary-foreground font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-bounce">
                    <MapPin className="h-4 w-4" />
                    {hoveredCity || currentRegion.name}
                  </div>
                  <div className="w-1 h-8 bg-gradient-to-b from-primary to-transparent" />
                  <div className="w-8 h-2 bg-primary/30 rounded-[100%] blur-sm" />
                </div>

                <iframe
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent((hoveredCity || currentRegion.name.replace(" Area", "")) + (currentRegion.name.includes("Phoenix") ? ", AZ" : ", CA"))}&t=&z=${hoveredCity ? "12" : "10"}&ie=UTF8&iwloc=&output=embed`}
                  title={`${currentRegion.name} Service Area Map`}
                  className="absolute inset-0 w-full h-full grayscale-[50%] contrast-[1.1] opacity-90 transition-all duration-700 ease-in-out group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
        
        {/* Zip Code Checker & Local SEO Explicit Regions List */}
        <div className="mt-24 pt-8 border-t border-border/50">
          <div className="mb-12">
            <ZipCodeChecker />
          </div>
          <Accordion type="single" collapsible className="w-full max-w-5xl mx-auto">
            <AccordionItem value="regions" className="border-none">
              <AccordionTrigger className="hover:no-underline justify-center gap-2 text-muted-foreground hover:text-foreground py-4">
                <span className="text-sm font-medium">View All Service Regions & Cities</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-8 pt-6 pb-4">
                  {Object.entries(REGIONS).map(([slug, region]) => (
                    <div key={slug} className="flex flex-col">
                      <h4 className="font-semibold text-sm mb-3 text-foreground flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-primary" />
                        {region.name}
                      </h4>
                      <ul className="space-y-1.5">
                        {region.cities.map(city => (
                          <li key={city}>
                            <Link 
                              to={`/locations/${city.toLowerCase().replace(/\s+/g, "-")}`}
                              className="text-xs text-muted-foreground hover:text-primary transition-colors"
                            >
                              {city} Cleaning
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </section>
  );
};
